import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { CircularProgress, Box, CssBaseline, ThemeProvider } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { QueryParamHandler } from './helpers/queryParamHandler';
import { userManager, authProvider as oidcAuthProvider } from './config/oidc';
import { mockAuthProvider } from './helpers/__mockAuthProvider';
import ErrorBoundary from './components/ErrorBoundary';
import { logEnvironmentValidation, validateEnvironment } from './utils/environment-validation';
import { getAppConfig } from './config/apps';
import { appDarkTheme, appLightTheme } from './styles/theme';
import './App.css'

// Use mock auth provider when VITE_TEST_ENV=mock (for e2e tests without real authentication).
// Must be VITE_-prefixed — Vite only forwards VITE_* env vars to the browser bundle.
const authProvider = import.meta.env.VITE_TEST_ENV === 'mock' ? mockAuthProvider : oidcAuthProvider;

// Lazy load components for code splitting
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const SignupPage = lazy(() => import('./components/signup/SignupPage'));
const LogoutPage = lazy(() => import('./components/LogoutPage'));
const OrgRegisterPage = lazy(() => import('./components/org-register/OrgRegisterPage'));

// App identity is fixed at build time, so this resolves once per module load.
const appConfig = getAppConfig();
const appDefaultTheme = appConfig.defaultThemeMode === 'dark' ? appDarkTheme : appLightTheme;
// Pages painted on brand.heroGradient need the mode that reads against the
// gradient, which is not always the app's default mode (see brand.heroThemeMode).
const appHeroTheme =
    (appConfig.brand.heroThemeMode ?? appConfig.defaultThemeMode) === 'dark'
        ? appDarkTheme
        : appLightTheme;

// The pages below render outside <Admin>, which is what supplies the theme (and
// its user-facing light/dark toggle) for everything under /*. Wrapping only
// these routes keeps the admin shell in sole charge of its own theming.
const PreAuthShell = ({ theme = appDefaultTheme, children }: { theme?: Theme; children: ReactNode }) => (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
    </ThemeProvider>
);

// Loading component for Suspense
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress size={60} />
  </Box>
);

const App = () => {
    // Validate environment configuration on startup
    useEffect(() => {
        if (import.meta.env.DEV) {
            logEnvironmentValidation();

            const validation = validateEnvironment();
            if (!validation.isValid) {
                console.error('Application may not function properly due to configuration errors');
            }
        }
    }, []);

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <QueryParamHandler />
                <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                        {/* Signup renders glass panels on the brand's hero
                            gradient, so it takes the hero's mode rather than the
                            app's — light for sirktek, dark for ledger17 and for
                            vardly, whose light-first app has a dark homepage hero. */}
                        <Route path="/signup" element={
                            <PreAuthShell theme={appHeroTheme}>
                                <SignupPage />
                            </PreAuthShell>
                        } />
                        {/* RA hardcodes its sign-in link to /login; redirect to / where checkAuth starts OIDC. */}
                        <Route path="/login" element={<Navigate to="/" replace />} />
                        <Route path="/org-register" element={
                            <PreAuthShell>
                                <OrgRegisterPage />
                            </PreAuthShell>
                        } />
                        <Route path="/logged-out" element={
                            <PreAuthShell>
                                <LogoutPage />
                            </PreAuthShell>
                        } />
                        <Route path="/*" element={
                            <AdminDashboard
                                userManager={userManager}
                                authProvider={authProvider}
                                apiBase={import.meta.env.VITE_API_ORGADMIN_API_BASE}
                            />
                        }/>
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </ErrorBoundary>
    );
};
export default App;